import { create } from 'zustand';
import posthog from 'posthog-js';
import type { User } from '../types';
import * as authService from '../services/auth';

// 개발자/관리자 이메일 - Posthog 추적에서 제외
const EXCLUDED_EMAILS = [
  'dbdb6151@gmail.com',
  'jiyun@baselabs.co.kr',
  'pelo1@jiyun.io',
  'xornjs6585@gmail.com',
];

// Posthog 사용자 식별 및 개발자 필터링
function identifyUserForAnalytics(user: User | null) {
  if (!user) return;

  if (EXCLUDED_EMAILS.includes(user.email)) {
    posthog.opt_out_capturing();
  } else {
    posthog.identify(user.id, {
      ...(user.email && { email: user.email }),
      ...(user.nickname && { nickname: user.nickname }),
    });
  }
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean; // 앱 초기화 중 (App.tsx에서 사용)
  isLoading: boolean; // 로그인/회원가입 등 작업 중 (컴포넌트에서 사용)
  error: string | null;

  initialize: () => Promise<void>;
  register: (email: string, password: string, nickname: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  handleOAuthCallback: (accessToken: string, refreshToken: string) => Promise<void>;
  updateNickname: (nickname: string) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isInitializing: true,
  isLoading: false,
  error: null,

  initialize: async () => {
    set({ isInitializing: true, error: null });
    try {
      const user = await authService.fetchCurrentUser();
      identifyUserForAnalytics(user);
      set({
        user,
        isAuthenticated: !!user,
        isInitializing: false,
      });
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isInitializing: false,
      });
    }
  },

  register: async (email: string, password: string, nickname: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register({ email, password, nickname });
      identifyUserForAnalytics(response.user);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: (error as Error).message,
        isLoading: false,
      });
      throw error;
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login({ email, password });
      identifyUserForAnalytics(response.user);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: (error as Error).message,
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    await authService.logout();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  handleOAuthCallback: async (accessToken: string, refreshToken: string) => {
    set({ isLoading: true, error: null });
    try {
      authService.handleOAuthCallback(accessToken, refreshToken);
      const user = await authService.fetchCurrentUser();
      identifyUserForAnalytics(user);
      set({
        user,
        isAuthenticated: !!user,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: (error as Error).message,
        isLoading: false,
      });
      throw error;
    }
  },

  updateNickname: async (nickname: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.updateProfile({ nickname });
      set((state) => ({
        user: state.user
          ? { ...state.user, nickname: response.nickname }
          : null,
        isLoading: false,
      }));
    } catch (error) {
      set({
        error: (error as Error).message,
        isLoading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
